import { useMutation } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import instance from '../api'
import { useAuthContext } from '../../context/auth/auth'

/**
 * `POST /mail/contact/` (mail.views.ContactUsView). Anonymous is allowed, but
 * the form is only reachable from the admin area, so the token is sent when
 * there is one.
 *
 * `full_name`, `email`, `subject`, `body` and `recipient_type` are required;
 * the serialiser also enforces minimum lengths (2 / 5 / 10), which the form
 * mirrors so the user sees the problem before the round trip. `phone_number`
 * and `company_name` are optional.
 *
 * `recipient_type` is the mailbox the message is routed to; the admin support
 * form is the only sender, so it is always `SUPPORT`.
 */
export interface SupportMessageRequest {
  full_name: string
  email: string
  subject: string
  body: string
  recipient_type: 'SUPPORT'
  phone_number?: string
  company_name?: string
}

const SUPPORT_ENDPOINT = '/mail/contact/'

const sendSupportMessage = async (
  data: SupportMessageRequest,
  token: string | null,
) => {
  const response = await instance.post(SUPPORT_ENDPOINT, data, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  })
  return response.data
}

// A 400 is `{ data: { field: ['message', ...] } }` after CustomJSONRenderer.
// Show the first message rather than a generic failure the user cannot act on.
const firstValidationError = (payload: unknown): string | undefined => {
  if (!payload || typeof payload !== 'object') return undefined
  for (const value of Object.values(payload as Record<string, unknown>)) {
    if (typeof value === 'string') return value
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0]
  }
  return undefined
}

const useSendSupportMessage = () => {
  const { t } = useTranslation()
  const { token } = useAuthContext()

  return useMutation({
    mutationFn: (data: SupportMessageRequest) =>
      sendSupportMessage(data, token),
    onSuccess: (response) => {
      // The message is stored before the email is attempted, so a send failure
      // still comes back 201 - with `error` instead of `message`.
      if (response?.data?.error) {
        toast.warning(response.data.error)
        return
      }
      toast.success(t('supportMessageSent'))
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const payload = error.response?.data?.data
        toast.error(
          firstValidationError(payload) ?? t('failedToSendSupportMessage'),
        )
        return
      }
      toast.error(t('failedToSendSupportMessage'))
    },
  })
}

export default useSendSupportMessage
